import { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'

function filterAdsFromM3U8(m3u8Content: string): string {
  if (!m3u8Content) return ''

  const lines = m3u8Content.split('\n')
  const filteredLines = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line.includes('#EXT-X-DISCONTINUITY')) {
      filteredLines.push(line)
    }
  }

  return filteredLines.join('\n')
}

interface PlayerProps {
  src?: string
  type?: 'hls' | 'mp4'
  headers?: Record<string, string>
  className?: string
}

export default function Player({ src, type, headers, className = '' }: PlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const objectUrlRef = useRef<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [isRecoverable, setIsRecoverable] = useState(false)
  const [lastSuccessUrl, setLastSuccessUrl] = useState<string | null>(null)
  const [proxyStrategy, setProxyStrategy] = useState(0) // 跟踪当前使用的代理策略

  // 智能重试逻辑
  const smartRetry = async (originalSrc: string, currentRetry: number) => {
    const maxRetries = 6 // 增加重试次数以覆盖更多策略
    const strategies = [
      // 策略1: 直接重试
      () => originalSrc,
      // 策略2: 添加时间戳强制刷新
      () => `${originalSrc}${originalSrc.includes('?') ? '&' : '?'}_t=${Date.now()}`,
      // 策略3: 切换代理端点 (hls <-> proxy)
      () => {
        if (originalSrc.includes('/hls?')) {
          // 切换到 /proxy 端点
          return originalSrc.replace('/hls?', '/proxy?')
        } else if (originalSrc.includes('/proxy?')) {
          // 切换回 /hls 端点
          return originalSrc.replace('/proxy?', '/hls?')
        }
        // 如果没有使用代理，尝试添加 /hls 代理
        if (originalSrc.includes('.m3u8') && !originalSrc.includes('/hls?') && !originalSrc.includes('/proxy?')) {
          return `/hls?url=${encodeURIComponent(originalSrc)}`
        }
        return originalSrc
      },
      // 策略4: 添加随机参数绕过缓存
      () => `${originalSrc}${originalSrc.includes('?') ? '&' : '?'}_r=${Math.random().toString(36).substr(2, 9)}`,
      // 策略5: 使用备用代理并添加重试参数
      () => {
        const baseUrl = originalSrc.replace(/[?&](retry|_t|_r|fallback)=[^&]*/g, '')
        if (baseUrl.includes('/hls?')) {
          return `${baseUrl}&retry=1&fallback=1`
        } else if (baseUrl.includes('/proxy?')) {
          return baseUrl.replace('/proxy?', '/hls?') + '&retry=1'
        }
        return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}retry=1`
      },
      // 策略6: 最后尝试：清除所有参数重新开始
      () => {
        const match = originalSrc.match(/url=([^&]+)/)
        if (match) {
          const encodedUrl = match[1]
          try {
            const decodedUrl = decodeURIComponent(encodedUrl)
            // 重新使用 /hls 代理
            return `/hls?url=${encodeURIComponent(decodedUrl)}&fresh=1`
          } catch {
            return originalSrc.replace(/[?&](_t|_r|fallback|retry|fresh)=[^&]*/g, '')
          }
        }
        return originalSrc.replace(/[?&](_t|_r|fallback|retry|fresh)=[^&]*/g, '')
      }
    ]

    if (currentRetry >= maxRetries) {
      return null
    }

    const strategyIndex = currentRetry % strategies.length
    const newUrl = strategies[strategyIndex]()

    console.log(`🔄 Smart retry ${currentRetry + 1}/${maxRetries}, strategy: ${strategyIndex + 1}, url: ${newUrl}`)

    // 添加指数退避延迟，避免频繁请求
    const delay = Math.min(500 * Math.pow(1.5, currentRetry), 8000) // 最大8秒
    await new Promise(resolve => setTimeout(resolve, delay))

    return newUrl
  }

  // 增强的错误恢复机制
  const attemptRecovery = async (originalSrc: string, errorData?: any) => {
    console.log('🚑 Attempting recovery for:', originalSrc, errorData)
    
    // 检查是否是可恢复的代理错误
    if (errorData?.context?.recoverable || errorData?.recoverable) {
      const newUrl = await smartRetry(originalSrc, retryCount)
      if (newUrl) {
        setRetryCount(prev => prev + 1)
        setError(null)
        setLoading(true)
        
        // 重新创建 HLS 实例
        if (hlsRef.current) {
          hlsRef.current.destroy()
          hlsRef.current = null
        }
        
        // 延迟加载新 URL
        setTimeout(() => {
          if (videoRef.current && newUrl) {
            videoRef.current.src = ''
            // 触发重新加载
            const event = new CustomEvent('srcchange', { detail: { newSrc: newUrl } })
            window.dispatchEvent(event)
          }
        }, 100)
        
        return true
      }
    }
    
    return false
  }

  // 监听自定义重试事件
  useEffect(() => {
    const handleSrcChange = (event: any) => {
      const newSrc = event.detail?.newSrc
      if (newSrc && videoRef.current) {
        console.log('📡 Switching to new URL:', newSrc)
        // 使用新的 URL 重新初始化播放器
        loadVideo(newSrc)
      }
    }

    window.addEventListener('srcchange', handleSrcChange)
    return () => window.removeEventListener('srcchange', handleSrcChange)
  }, [type, headers])

  // 抽取视频加载逻辑
  const loadVideo = (videoSrc: string) => {
    if (!videoRef.current) return

    const video = videoRef.current
    setError(null)
    setLoading(true)

    // Clean up previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }

    // Release old blob URL (if any)
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }

    if (type === 'hls' && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 30,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        manifestLoadingTimeOut: 30000, // 增加超时时间
        manifestLoadingMaxRetry: 5, // 增加重试次数
        levelLoadingTimeOut: 30000,
        levelLoadingMaxRetry: 5,
        fragLoadingTimeOut: 30000,
        fragLoadingMaxRetry: 5,
        // 增强的错误恢复配置
        manifestLoadingRetryDelay: 1000,
        levelLoadingRetryDelay: 1000,
        fragLoadingRetryDelay: 1000,
        xhrSetup: (xhr) => {
          if (headers) {
            for (const [key, value] of Object.entries(headers)) {
              try { 
                xhr.setRequestHeader(key, value) 
              } catch (e) {
                console.warn(`Failed to set header ${key}:`, e)
              }
            }
          }
        },
        // 增强的加载器处理
        loader: class extends Hls.DefaultConfig.loader {
          constructor(config: any) {
            super(config)
            const originalLoad = this.load.bind(this)
            this.load = function (context: any, config: any, callbacks: any) {
              const originalOnSuccess = callbacks.onSuccess
              callbacks.onSuccess = function (response: any, stats: any, context: any) {
                // 检查代理错误响应
                if (response.data && typeof response.data === 'string') {
                  try {
                    const parsed = JSON.parse(response.data)
                    if (parsed.error) {
                      console.error('🚨 HLS Proxy Error:', parsed)
                      
                      // 特殊处理404错误
                      if (parsed.status === 404) {
                        console.log('🔄 404 error detected, will try alternative strategies')
                        // 标记为可恢复错误，让播放器尝试其他策略
                        callbacks.onError({
                          type: 'networkError',
                          details: '视频源暂时不可用',
                          fatal: true,
                          reason: parsed.suggestion || '请尝试其他集数或刷新页面',
                          context: parsed,
                          recoverable: true
                        }, stats, context)
                        return
                      }
                      
                      const isRecoverableError = parsed.recoverable || parsed.suggestion?.includes('重试')
                      
                      callbacks.onError({
                        type: 'networkError',
                        details: parsed.error,
                        fatal: !isRecoverableError,
                        reason: parsed.details || parsed.error,
                        context: parsed,
                        recoverable: isRecoverableError
                      }, stats, context)
                      return
                    }
                  } catch (e) {
                    // Not JSON, continue processing
                  }
                  
                  // 过滤广告并记录成功的 URL
                  response.data = filterAdsFromM3U8(response.data)
                  setLastSuccessUrl(videoSrc)
                }
                return originalOnSuccess(response, stats, context)
              }
              
              const originalOnError = callbacks.onError
              callbacks.onError = function (error: any, stats: any, context: any) {
                console.error('🔥 HLS Loader Error:', error, context)
                
                // 智能错误分类和恢复
                if (error.code === 404 || (error.response && error.response.status === 404)) {
                  error.recoverable = true
                  error.reason = '视频源暂时不可用'
                  error.details = '正在尝试切换线路'
                } else if (error.code === 403 || (error.response && error.response.status === 403)) {
                  error.recoverable = true
                  error.reason = '访问受限'
                  error.details = '正在尝试使用代理访问'
                } else if (String(error.message || '').includes('timeout')) {
                  error.recoverable = true
                  error.reason = '连接超时'
                  error.details = '网络较慢，正在重试'
                }
                
                return originalOnError(error, stats, context)
              }
              
              originalLoad(context, config, callbacks)
            }
          }
        }
      })
      
      hls.loadSource(videoSrc)
      hls.attachMedia(video)
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('✅ HLS manifest parsed successfully')
        setLoading(false)
        setLastSuccessUrl(videoSrc)
        setRetryCount(0) // 重置重试计数
        video.play().catch((e) => {
          console.error('Auto-play failed:', e)
          setError('自动播放失败，请手动点击播放')
        })
      })
      
      hls.on(Hls.Events.ERROR, async (event, data) => {
        console.error('🔥 HLS Error Event:', { event, data, url: videoSrc })
        setLoading(false)

        const proxyContext = data.context
        const isRecoverableError = data.recoverable || proxyContext?.recoverable
        const maxRetries = 6 // 统一的最大重试次数

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              const errorMsg = data.reason || data.details || '网络错误'

              if (isRecoverableError && retryCount < maxRetries) {
                console.log(`🔄 Attempting automatic recovery (${retryCount + 1}/${maxRetries})`)
                setIsRecoverable(true)
                setError(`${proxyContext?.error || errorMsg} - 正在自动重试...`)

                // 自动尝试恢复
                const recovered = await attemptRecovery(videoSrc, data)
                if (!recovered) {
                  setError(`网络错误: ${errorMsg}`)
                }
              } else {
                setError(`网络错误: ${errorMsg}`)
                // 非代理错误的标准重试
                if (!data.reason || !data.reason.includes('内容暂时不可用')) {
                  setTimeout(() => {
                    if (hlsRef.current) {
                      console.log('🔧 Attempting network recovery...')
                      hlsRef.current.startLoad()
                    }
                  }, 3000)
                }
              }
              break

            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('🎬 Media error detected, attempting recovery...')
              setError('媒体错误，尝试恢复中...')
              setTimeout(() => {
                if (hlsRef.current) {
                  console.log('🔧 Attempting media recovery...')
                  hlsRef.current.recoverMediaError()
                }
              }, 1000)
              break

            default:
              setError(`播放失败: ${data.reason || data.details || '未知错误'}`)
              hls.destroy()
              hlsRef.current = null
              break
          }
        } else {
          console.warn('⚠️  Non-fatal HLS error:', data)
        }
      })

      // 添加更多事件监听以提供更好的用户反馈
      hls.on(Hls.Events.MANIFEST_LOADING, () => {
        setLoading(true)
        console.log('📄 Loading manifest...')
      })

      hls.on(Hls.Events.LEVEL_LOADED, () => {
        setLoading(false)
        console.log('📋 Level loaded successfully')
      })

      hls.on(Hls.Events.FRAG_LOADING, () => {
        // 片段加载中，可以显示缓冲状态
      })

      hls.on(Hls.Events.FRAG_LOADED, () => {
        // 片段加载完成
        if (error && isRecoverable) {
          setError(null)
          setIsRecoverable(false)
        }
      })
      
      hlsRef.current = hls
    } else if (type === 'mp4' || !type) {
      // MP4 播放逻辑保持不变
      const playDirect = async () => {
        try {
          setLoading(true)
          if (headers && Object.keys(headers).length > 0) {
            const response = await fetch(videoSrc, { headers })
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }
            const blob = await response.blob()
            const objectUrl = URL.createObjectURL(blob)
            objectUrlRef.current = objectUrl
            video.src = objectUrl
          } else {
            video.src = videoSrc
          }
          video.load()
          setLoading(false)
          await video.play()
          setLastSuccessUrl(videoSrc)
        } catch (error) {
          console.error('Video playback error:', error)
          setLoading(false)
          setError(`播放失败: ${error}`)
        }
      }
      void playDirect()
    } else if (type === 'hls' && !Hls.isSupported()) {
      // 原生 HLS 播放
      setLoading(true)
      video.src = videoSrc
      video.load()
      video.play().then(() => {
        setLoading(false)
        setLastSuccessUrl(videoSrc)
      }).catch((error) => {
        console.error('Native HLS playback failed:', error)
        setLoading(false)
        setError('浏览器不支持HLS播放')
      })
    }
  }

  useEffect(() => {
    if (!src || !videoRef.current) return

    setError(null)
    setLoading(true)
    setRetryCount(0)
    setIsRecoverable(false)

    // 使用抽取的加载函数
    loadVideo(src)

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [src, type, headers])

  if (!src) {
    return (
      <div className={`bg-gray-900 flex items-center justify-center text-gray-400 ${className}`}>
        <div className="text-center">
          <div className="text-6xl mb-4">▶️</div>
          <div>暂无播放内容</div>
        </div>
      </div>
    )
  }

  const handleRetry = async () => {
    const maxRetries = 6 // 与 smartRetry 中的 maxRetries 保持一致
    if (retryCount < maxRetries) {
      console.log(`🔄 Manual retry requested (${retryCount + 1}/${maxRetries})`)
      const newUrl = await smartRetry(src || '', retryCount)
      if (newUrl) {
        setRetryCount(prev => prev + 1)
        setError(null)
        setLoading(true)
        loadVideo(newUrl)
      } else {
        setError('已达到最大重试次数，请稍后再试')
      }
    }
  }

  const handleFallbackRetry = () => {
    console.log('🔄 Fallback retry requested - forcing page reload')
    // 如果有成功的URL，尝试使用它
    if (lastSuccessUrl && lastSuccessUrl !== src) {
      loadVideo(lastSuccessUrl)
    } else {
      // 最后的手段：刷新页面
      window.location.reload()
    }
  }

  if (error) {
    const maxRetries = 6 // 统一的最大重试次数
    const canRetry = retryCount < maxRetries
    const hasAlternative = lastSuccessUrl && lastSuccessUrl !== src

    return (
      <div className={`bg-gray-900 flex items-center justify-center text-red-400 ${className}`}>
        <div className="text-center p-6 max-w-md">
          <div className="text-5xl mb-4">
            {isRecoverable ? '🔄' : '⚠️'}
          </div>

          <div className="mb-3">
            <h3 className="text-lg font-semibold mb-2">
              {isRecoverable ? '正在重试连接...' : '播放出错'}
            </h3>
            <div className="text-sm text-gray-400 mb-2">{error}</div>
          </div>

          {/* 重试进度指示器 */}
          {retryCount > 0 && (
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">
                已尝试 {retryCount}/{maxRetries} 次
              </div>
              <div className="w-full bg-gray-700 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${(retryCount / maxRetries) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {/* 主要重试按钮 */}
            {(isRecoverable || canRetry) && (
              <button
                onClick={handleRetry}
                disabled={!canRetry || loading}
                className={`w-full px-4 py-2 rounded font-medium transition-all ${
                  canRetry && !loading
                    ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    重试中...
                  </span>
                ) : (
                  `智能重试 ${canRetry ? `(${maxRetries - retryCount}次机会)` : '(已达上限)'}`
                )}
              </button>
            )}

            {/* 备用重试按钮 */}
            {hasAlternative && (
              <button
                onClick={handleFallbackRetry}
                className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
              >
                🔄 尝试上次成功的链接
              </button>
            )}

            {/* 强制刷新按钮 */}
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 font-medium"
            >
              🔄 重新加载页面
            </button>

            {/* 调试信息（仅在开发环境显示） */}
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-400">
                  调试信息
                </summary>
                <div className="mt-2 text-xs text-gray-600 bg-gray-800 p-2 rounded">
                  <div>当前重试: {retryCount}/{maxRetries}</div>
                  <div>可恢复: {isRecoverable ? '是' : '否'}</div>
                  <div>成功URL: {lastSuccessUrl || '无'}</div>
                  <div>当前策略: {proxyStrategy}</div>
                  <div>原始URL: {src?.substring(0, 50)}...</div>
                </div>
              </details>
            )}
          </div>

          {/* 额外提示 */}
          <div className="mt-4 text-xs text-gray-500">
            {isRecoverable
              ? '系统正在自动尝试不同的连接方式'
              : retryCount > 3
                ? '如果问题持续存在，可能是视频源暂时不可用'
                : '网络连接可能不稳定，建议检查网络状态'
            }
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative ${className}`}>
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
            <div>加载中...</div>
          </div>
        </div>
      )}
      <video
        ref={videoRef}
        className="w-full h-full object-contain bg-black"
        controls
        muted
        playsInline
        poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjMTExMTExIi8+CjxwYXRoIGQ9Ik0xNDAgOTBMMjAwIDEzMEwxNDAgMTcwVjkweiIgZmlsbD0iI0ZGRkZGRiIvPgo8L3N2Zz4K"
      >
        <source src={src} type={type === 'hls' ? 'application/x-mpegURL' : 'video/mp4'} />
        您的浏览器不支持视频播放
      </video>
    </div>
  )
}